import { useCallback, useMemo } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import {
  ParticipantField,
  ParticipantFieldCategory,
  useParticipantFieldsCategory,
} from '@app/api';
import { Criterion } from 'queries';
import { CriterionChip } from './CriterionChip';
import { CriterionValueSelection } from './CriterionValueSelection';
import { createEmptyCriterionFromParticipantField } from 'lib/criteria';
import { TargetingSection } from '../TargetingSection';
import { isFieldHandledForCriterion } from './criterionFormFields';
import { isFieldAllowed } from 'lib/criteriaUtils';

const useStyles = createUseStyles((theme: Theme) => ({
  chipsContainer: {
    display: 'flex',
    flexWrap: 'wrap',

    backgroundColor: theme.colors.grayBackground,
    borderRadius: theme.borderRadius.std,

    padding: theme.marginBase * 2,
    paddingRight: theme.marginBase,
    paddingBottom: theme.marginBase,
  },
}));

interface Props {
  category: ParticipantFieldCategory;
  fields: ParticipantField[];
  allowedFields?: string[];
  criteria: Criterion[];
  onCriteriaChange: (criteria: Criterion[]) => any;
  onRequestUpsellModalOpen?: () => void;
  numberOfParticipantRequired: number;
}

export const TargetingCriteriaSelectionSection = ({
  category,
  fields,
  allowedFields,
  criteria,
  onCriteriaChange,
  onRequestUpsellModalOpen,
  numberOfParticipantRequired,
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });

  const categoryConfig = useParticipantFieldsCategory(category);

  const addCriteria = (field: ParticipantField) => {
    onCriteriaChange([
      ...criteria,
      createEmptyCriterionFromParticipantField(field),
    ]);
  };

  const removeCriterion = useCallback(
    (criterion: Criterion) => {
      onCriteriaChange(criteria.filter((c) => c !== criterion));
    },
    [criteria, onCriteriaChange],
  );

  const onCriterionChanged = useCallback(
    (criterion: Criterion) => {
      onCriteriaChange(
        criteria.map((c) => {
          if (c.fieldRef === criterion.fieldRef) {
            return criterion;
          }
          return c;
        }),
      );
    },
    [criteria, onCriteriaChange],
  );

  const addableChips = useMemo(() => {
    if (!fields) {
      return [];
    }
    return fields.filter((field) => {
      const selected = !!criteria.find(
        (criterion) => criterion.fieldRef === field.name,
      );
      const handled = isFieldHandledForCriterion(field);
      return !selected && handled;
    });
  }, [fields, criteria]);

  if (!categoryConfig) {
    return null;
  }

  return (
    <TargetingSection
      title={categoryConfig.titleClient || categoryConfig.title}
    >
      {!!addableChips.length && (
        <div className={classes.chipsContainer}>
          {addableChips.map((field: ParticipantField) => (
            <CriterionChip
              key={field.name}
              allowed={isFieldAllowed(allowedFields, field)}
              addCriteria={addCriteria}
              field={field}
              disabled={field?.criterion?.disabled}
              onForbiddenClick={onRequestUpsellModalOpen}
            />
          ))}
        </div>
      )}
      {criteria.map((criterion) => (
        <CriterionValueSelection
          key={criterion.id}
          criterion={criterion}
          field={fields?.find((field) => criterion.fieldRef === field.name)}
          removeCriterion={removeCriterion}
          onCriterionChanged={onCriterionChanged}
          criteria={criteria}
          allowedFields={allowedFields}
          numberOfParticipantRequired={numberOfParticipantRequired}
        />
      ))}
    </TargetingSection>
  );
};
